// customerQuote.ts — 고객 제출용 견적서(이슈 #110) 연동 매핑 + 견적번호 변환 (순수).
//
// 내부 견적 상세(DetailScreen)와 분리된 외부 제출 문서. 여기서는 견적 등록 데이터에서
// "연동" 값(견적번호·제목·작성일/자·품목 리스트)을 끌어와 프리필 형태로 평면화한다.
// 직접 입력 값(수신·참조·이미지·기타)은 component state + customerDoc 영속이 담당.
// 비고는 견적 등록의 비고(quote.remark)와 양방향 연동 — prefillCustomerDoc 참고.
// 새 원가 수식 금지 — 견적가/공법/재질 등은 cncBreakdown(이미 계산된 result)에서 픽한다.

import {
  type BreakdownRow,
  type PartLabel,
  cncBreakdown,
  partAvgVolume,
} from "@/lib/cost/cncBreakdown";
import type { CncPart, CustomerDoc, Quote } from "@/lib/data";

// 견적번호 변환: QT-YYYYMMDD-NNN → HBM-YYYYMMDD-NN (한부금속 = HanBu Metal).
// 끝 일련번호는 3자리 → 2자리(앞자리 0 제거 후 2자리 zero-pad). QT 형식이 아니면
// 접두만 HBM- 로 바꿔 원본 꼬리를 보존한다(연동값도 화면에서 수정 가능하므로 fail-soft).
export function customerDocNo(quote: Pick<Quote, "id">): string {
  const id = quote.id ?? "";
  const m = id.match(/^QT-(\d{8})-(\d+)$/);
  if (!m) return id.startsWith("HBM-") ? id : `HBM-${id.replace(/^QT-/, "")}`;
  const seq = String(Number.parseInt(m[2], 10)).padStart(2, "0");
  return `HBM-${m[1]}-${seq}`;
}

// 품목 한 행(연동) — 견적가 단가/품명/공법/재질/연간수량. 도금은 표면처리 라인명으로 추정.
export interface CustomerQuoteItem {
  itemName: string; // 품명
  image: string; // 이미지 — 견적등록 파트 이미지(스토리지 키). 없으면 "" (직접 입력 대상).
  method: string; // 공법 — 견적유형 라벨(CNC/단조/단조(외주)), 미지정 시 공정명 fallback
  unitPrice: number; // 견적가(원) 단가
  material: string; // 재질
  plating: string; // 도금
  annualQty: number; // 연간 수량(EA) — 평균 물량
}

// 도금 = 표면처리 라인 라벨 join. 없으면 "" (직접 입력 대상으로 비워둠).
function platingOf(part: CncPart): string {
  return (part.selection.surfaceLines ?? [])
    .map((l) => l.label.trim())
    .filter((s) => s.length > 0)
    .join(", ");
}

// 연간 수량 = (연도값 × USG) 입력 연도만 평균. partAvgVolume 공용(연평균 자동계산과 동일 기준).
function annualQtyOf(part: CncPart): number {
  return Math.round(partAvgVolume(part));
}

// 연동 품목 리스트 — cncBreakdown 으로 견적가/공법/재질 픽 + 도금/연간수량 결합.
export function customerQuoteItems(parts: CncPart[], labels: PartLabel[]): CustomerQuoteItem[] {
  const rows: BreakdownRow[] = cncBreakdown(parts, labels).rows;
  return parts.map((part, i) => ({
    itemName: rows[i]?.itemName || rows[i]?.partNo || "",
    image: part.selection.partImage ?? "",
    method: rows[i]?.quoteTypeLabel ?? "",
    unitPrice: rows[i]?.supplyPrice ?? 0,
    material: rows[i]?.material ?? "",
    plating: platingOf(part),
    annualQty: annualQtyOf(part),
  }));
}

// 프리필/보존 병합 — 견적을 처음 열 때만 저장본(customerDoc)으로 초기화하고, 같은 견적을
// 다시 조회(상태 변경 후 reload, 마스터 로드 완료 등)할 때는 편집 중인 값을 그대로 유지한다.
// 재조회마다 초기화하면 저장 전 입력이 전부 날아가 재입력해야 했다(이슈: 제출 표지 값 유실).
// items 는 품목 수와 1:1 이어야 하므로 길이만 항상 맞춘다.
// 비고는 예외 — 견적 등록 화면의 비고(quote.remark)가 단일 원천이라 저장본이 있어도 항상 그 값을
// 따른다(표지 저장 시 quote.remark 에도 함께 기록). 저장본 비고를 우선하면 표지를 한 번 저장한 뒤
// 견적 등록에서 바꾼 비고가 표지에 연동되지 않았다.
export function prefillCustomerDoc(args: {
  saved?: CustomerDoc;
  quoteRemark: string;
  itemCount: number;
  editing?: CustomerDoc; // 편집 중 값(재조회 시). 없으면 최초 로드로 보고 저장본 사용.
}): CustomerDoc {
  const { saved, quoteRemark, itemCount, editing } = args;
  const src: CustomerDoc = editing ?? {
    recipient: saved?.recipient ?? "",
    reference: saved?.reference ?? "",
    remark: quoteRemark,
    overrides: saved?.overrides ?? {},
    items: saved?.items ?? [],
  };
  return {
    ...src,
    items: Array.from({ length: itemCount }, (_, i) => ({
      image: src.items[i]?.image ?? "",
      etc: src.items[i]?.etc ?? "",
    })),
  };
}

// 수신 자동 조합 — "고객사명 담당자"(예: "건희EnG 이종혁 매니저"). 견적의 담당자(quote.owner)는
// 고객사 연락처에서 고르는 값이라 그대로 수신처가 된다. 사용자가 입력하면 그 값이 우선(연동값+override).
export function autoRecipient(customerName: string, owner: string): string {
  const name = customerName === "—" ? "" : customerName.trim();
  return [name, owner.trim()].filter(Boolean).join(" ");
}
